import React, { useState, useCallback, memo, useEffect } from 'react';
import { Modal, Tabs, Tab, Alert, Button, Row, Col } from 'react-bootstrap';
import { MatchAdmin, MatchStatus } from '../../models/interfaces/Match';
import ModalManageStatDisplay from './modules/ModalManageStatDisplay';
import RefSummaryView from './modules/RefSummaryView';
import TimelineView from './modules/TimelineView';
import FinishMatchFooter from './modules/FinishMatchFooter';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { MatchSegmentStats, Stat } from '../../models/interfaces/Stat';
import useGetMatchEntryPlayers from '../../helpers/useGetRosterEntry';
import MatchPlayerView from './modules/MatchPlayerView';
import BulkStatRow from './modules/BulkStatRow';

interface Props {
    shouldShow: boolean;
    onHideModal: () => void;
    matchAdmin: MatchAdmin;
    matchStatsList: MatchSegmentStats[];
    sportProvider: SportProvider;
    triggerUpdatedMatchStats: () => void;
    editMatchStats: boolean;
    finalScoreEntered: boolean;
}

type tabKeyType = 'manageStats' | 'summary' | 'timeline' | 'player' | 'bulk';

const MatchStatsModal = memo(
    ({
        shouldShow,
        onHideModal,
        matchAdmin,
        matchStatsList,
        sportProvider,
        triggerUpdatedMatchStats,
        editMatchStats,
        finalScoreEntered,
    }: Props) => {
        const [tabKey, setTabKey] = useState<tabKeyType>(
            editMatchStats ? 'manageStats' : 'summary'
        );
        const {
            teamPlayers: lightCapPlayerList,
            triggerUpdatedPlayerList: triggerUpdatedLightCapPlayerList,
        } = useGetMatchEntryPlayers({
            team: matchAdmin.match.lightCapTeam,
            match: matchAdmin.match,
        });
        const {
            teamPlayers: darkCapPlayerList,
            triggerUpdatedPlayerList: triggerUpdatedDarkCapPlayerList,
        } = useGetMatchEntryPlayers({
            team: matchAdmin.match.darkCapTeam,
            match: matchAdmin.match,
        });

        // only used to be able to go back to prev tab of cancel finish match
        const [prevTabKey, setPrevTabKey] = useState<tabKeyType>('manageStats');
        const [editingStat, setEditingStat] = useState<Stat | undefined>(undefined);
        const [confirmFinishMatch, setConfirmFinishMatch] = useState<boolean>(false);
        const [showFinalScoreAlert, setShowFinalScoreAlert] = useState<boolean>(finalScoreEntered);

        const handleEditSelectedStat = useCallback((stat: Stat) => {
            setEditingStat(stat);
            setTabKey('manageStats');
        }, []);

        const promptFinishMatchConfirmation = () => {
            setPrevTabKey(tabKey);
            setConfirmFinishMatch(true);
            setTabKey('summary');
        };

        const cancelPromptFinishMatchConfirmation = () => {
            setConfirmFinishMatch(false);
            setTabKey(prevTabKey);
        };

        const resetConfirmFinishMatch = () => {
            setConfirmFinishMatch(false);
        };

        // used to display edit & complete buttons & also show warning if match is previous complete
        useEffect(() => {
            if (editMatchStats) {
                setTabKey('manageStats');
                if (matchAdmin.match.status === MatchStatus.completed) {
                    if (finalScoreEntered) setShowFinalScoreAlert(true);
                }
            } else {
                setTabKey('summary');
                setShowFinalScoreAlert(false);
            }
        }, [editMatchStats, matchAdmin.match.status, finalScoreEntered]);

        const handleClose = () => {
            if (matchAdmin.match.status === MatchStatus.completed && editMatchStats) {
                if (finalScoreEntered) setShowFinalScoreAlert(true);
            }
            if (editingStat) setEditingStat(undefined);
            onHideModal();
        };

        return (
            <Modal show={shouldShow} onHide={handleClose} size="xl" backdrop='static'>
                <Modal.Header closeButton onHide={handleClose}>
                    <Modal.Title>Official Match Stats</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showFinalScoreAlert ? (
                        <Alert
                            variant="warning"
                            onClose={() => setShowFinalScoreAlert(false)}
                            dismissible
                        >
                            <p>
                                A final score was entered for this match without inputting stats.
                                Entering a stat will clear the previously entered score and the new
                                score will be based upon the stats.
                            </p>

                            <Button onClick={handleClose} variant="outline-primary">
                                Close
                            </Button>
                            <Button
                                className="mx-2"
                                onClick={() => setShowFinalScoreAlert(false)}
                                variant="danger"
                            >
                                Proceed
                            </Button>
                        </Alert>
                    ) : (
                        <Tabs
                            id="match-stat-tab-controller"
                            activeKey={tabKey}
                            onSelect={(key) => {
                                switch (key) {
                                    case 'manageStats':
                                        setTabKey('manageStats');
                                        break;
                                    case 'summary':
                                        setTabKey('summary');
                                        break;
                                    case 'timeline':
                                        setTabKey('timeline');
                                        break;
                                    case 'player':
                                        setTabKey('player');
                                        break;
                                    case 'bulk':
                                        setTabKey('bulk')
                                        break;
                                    default:
                                        setTabKey('manageStats');
                                }
                            }}
                        >
                            {!confirmFinishMatch && editMatchStats && (
                                <Tab eventKey="manageStats" title="Record Match">
                                    <Row>
                                        <Col xs={12} sm={12} md={12} lg={7} xl={6}>
                                            <ModalManageStatDisplay
                                                matchAdmin={matchAdmin}
                                                editingStat={editingStat}
                                                setEditingStat={setEditingStat}
                                                tabSelected={tabKey === 'manageStats'}
                                                triggerUpdatedMatchStats={triggerUpdatedMatchStats}
                                                lightCapPlayerList={lightCapPlayerList}
                                                darkCapPlayerList={darkCapPlayerList}
                                                existingStats={matchStatsList}
                                            />
                                        </Col>
                                        <Col xs={12} sm={12} md={12} lg={5} xl={6}>
                                        <RefSummaryView
                                            matchAdmin={matchAdmin}
                                            matchStatsList={matchStatsList}
                                            sportProvider={sportProvider}
                                        />
                                        </Col>
                                    </Row>
                                </Tab>
                            )}
                            {/* editingStat to be checked for each as Tab's can't be children of fragments */}
                            {!editingStat && (
                                <Tab eventKey="summary" title="Summary">
                                    <RefSummaryView
                                        matchAdmin={matchAdmin}
                                        matchStatsList={matchStatsList}
                                        sportProvider={sportProvider}
                                    />
                                </Tab>
                            )}
                            {!editingStat && (
                                <Tab eventKey="timeline" title="Timeline">
                                    <TimelineView
                                        matchStatsList={matchStatsList}
                                        matchAdmin={matchAdmin}
                                        handleEditSelectedStat={handleEditSelectedStat}
                                        editMatchStats={editMatchStats}
                                    />
                                </Tab>
                            )}
                            {!editingStat && (
                                <Tab eventKey="player" title="Players">
                                    <MatchPlayerView
                                        matchAdmin={matchAdmin}
                                        lightCapPlayerList={lightCapPlayerList}
                                        triggerUpdatedLightCapPlayerList={
                                            triggerUpdatedLightCapPlayerList
                                        }
                                        darkCapPlayerList={darkCapPlayerList}
                                        triggerUpdatedDarkCapPlayerList={
                                            triggerUpdatedDarkCapPlayerList
                                        }
                                        sportProvider={sportProvider}
                                    />
                                </Tab>
                            )}
                            {
                                !editingStat && (
                                    <Tab eventKey="bulk" title="Bulk Operations">
                                        <BulkStatRow 
                                            matchAdmin={matchAdmin}
                                            matchStatsList={matchStatsList}
                                            triggerUpdatedMatchStats={triggerUpdatedMatchStats}
                                        />
                                    </Tab>
                                )
                            }
                        </Tabs>
                    )}
                </Modal.Body>
                {!editingStat && editMatchStats && !showFinalScoreAlert && (
                    // need's to be display block (d-block) as we use row's within the component that are display flex
                    <Modal.Footer className="justify-content-center d-block">
                        <FinishMatchFooter
                            matchAdmin={matchAdmin}
                            onHideModal={onHideModal}
                            confirmFinishMatch={confirmFinishMatch}
                            cancelPromptFinishMatchConfirmation={
                                cancelPromptFinishMatchConfirmation
                            }
                            promptFinishMatchConfirmation={promptFinishMatchConfirmation}
                            resetConfirmFinishMatch={resetConfirmFinishMatch}
                        />
                    </Modal.Footer>
                )}
            </Modal>
        );
    }
);

export default MatchStatsModal;
